<template>
  <PageBreadcrumbs :items="breadcrumbs" />
  <ContentCard data-testid="agency-collect-card" class="mb-8">
    <template #header>
      <ContentCardSubheader>
        {{ t('Reviews.agency.reviews.title') }}
      </ContentCardSubheader>
    </template>

    <div v-if="loading || hasReviews || hasActiveFilters" class="mb-2 flex items-start gap-20 py-4">
      <div class="w-full">
        <p class="txt-headline-m-bold">{{ t('Reviews.agency.reviews.collectTitle', { agencyName: agency.title }) }}</p>
        <p class="mt-1 max-w-xl txt-body-s-regular text-subtle" v-html="t('Reviews.agency.reviews.collectDescription')"></p>
      </div>
      <GenericInvitationLinks subject="agency" :slug="agency.slug" cta-position="agency_reviews" orientation="column" />
    </div>
    <div v-else class="m-auto mb-10 max-w-xl py-10 text-center" data-testid="agency-reviews-empty">
      <StarIcon class="mx-auto mb-4 lucide-icon-l text-icon-subtlest" aria-hidden="true" />
      <h3 class="mt-2">{{ t('Reviews.agency.reviews.table.emptyTitle') }}</h3>
      <p class="mt-1 txt-body-s-regular text-subtle" v-html="t('Reviews.agency.reviews.table.emptyDescription')"></p>
      <div class="mt-8 inline-flex px-6 py-4 text-left">
        <GenericInvitationLinks subject="agency" :slug="agency.slug" cta-position="agency_reviews" orientation="column" />
      </div>
    </div>
  </ContentCard>

  <ContentCard v-if="loading || hasReviews || hasActiveFilters" data-testid="agency-reviews-card">
    <template #header>
      <ContentCardSubheader>
        {{ t('Reviews.agency.reviewsTable.reviewsTitle') }}
        <template #actions>
          <button class="btn-outline-color-s" :disabled="!hasReviews" data-testid="export-reviews-button" @click="downloadCsv">
            {{ t('Reviews.agency.action.downloadReviewsCsv', { count: pagination.total }) }}
            <DownloadIcon class="lucide-icon-s" />
          </button>
        </template>
      </ContentCardSubheader>
    </template>

    <AgencyReviewsFilters v-if="hasReviews || hasActiveFilters" />

    <div v-if="loading" class="flex justify-center py-12">
      <CircleSpinner />
    </div>
    <div v-else-if="hasReviews" class="pb-6">
      <AgencyReviewsTable :reviews="reviews" />
      <PaginationControl
        :page="pagination.page"
        :per-page="pagination.perPage"
        :total="pagination.total"
        show-range
        show-total
        show-pagination="always"
        @update:page="updatePage"
        @update:per-page="updatePerPage"
      />
    </div>
    <div v-else-if="hasActiveFilters" class="m-auto mb-10 max-w-xl py-10 text-center" data-testid="agency-reviews-empty-filtered">
      <h3 class="mt-2">{{ t('Reviews.agency.reviews.filters.emptyResultsTitle') }}</h3>
      <p class="mt-1 txt-body-s-regular text-subtle">{{ t('Reviews.agency.reviews.filters.emptyResultsText') }}</p>
      <button class="btn-outline-color-xs mt-8" data-testid="agency-reviews-clear-filters" @click="resetFilters">
        {{ t('Reviews.agency.reviews.filters.clearAllButton') }}
      </button>
    </div>
  </ContentCard>
</template>

<script setup lang="ts">
import { CircleSpinner } from '@ramp106/omrjs-core-ui'
import { DownloadIcon, StarIcon } from '@ramp106/omrjs-core-ui/icons'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import PageBreadcrumbs from '@/components/PageBreadcrumbs.vue'
import PaginationControl from '@/components/PaginationControl.vue'
import AgencyReviewsFilters from '@/components/ReviewsAgenciesManagements/Reviews/AgencyReviewsFilters.vue'
import AgencyReviewsTable from '@/components/ReviewsAgenciesManagements/Reviews/AgencyReviewsTable.vue'
import { useRouterQueryAgencyReviewsFilters } from '@/components/ReviewsAgenciesManagements/Reviews/useRouterQueryAgencyReviewsFilters'
import GenericInvitationLinks from '@/components/ReviewsManagement/Reviews/GenericInvitationLinks.vue'
import { useReviewsAgencyBreadcrumbs } from '@/composables/breadcrumbs/useReviewsAgencyBreadcrumbs'
import { useRouterQueryPagination } from '@/composables/useRouterQueryPagination'
import type { AgencyData } from '@/gql/reviews'
import {
  ManageUniversalReviewsSortByEnum,
  SortDirectionEnum,
  useReviewsAgencyUniversalReviewsExportCsvLazyQuery,
  useReviewsAgencyUniversalReviewsQuery,
} from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper'
import ContentCard from '@/ui/ContentCard.vue'
import ContentCardSubheader from '@/ui/ContentCardSubheader.vue'
import { useSortQuery } from '@/ui/TableLight'

const props = defineProps<{ agency: AgencyData }>()
const { t } = useI18n()
const breadcrumbs = useReviewsAgencyBreadcrumbs(props.agency)
const { page, perPage, updatePage, updatePerPage } = useRouterQueryPagination()
const { sortBy, sortDirection } = useSortQuery(ManageUniversalReviewsSortByEnum, SortDirectionEnum)
const { ratings, states, dateRange, hasActiveFilters, resetFilters } = useRouterQueryAgencyReviewsFilters()

const variables = computed(() => ({
  subjectId: props.agency.globalId,
  page: page.value,
  perPage: perPage.value,
  sortBy: sortBy.value,
  sortDirection: sortDirection.value,
  filters: {
    rating: ratings.value.length ? ratings.value : undefined,
    status: states.value.length ? states.value : undefined,
    updatedSince: dateRange.value.updatedSince,
    updatedTo: dateRange.value.updatedTo,
  },
}))
const { loading, result } = useReviewsAgencyUniversalReviewsQuery(variables, { clientId: 'reviews' })
const reviews = computed(() => result.value?.manage?.universalReviews?.reviews ?? [])
const hasReviews = computed(() => reviews.value.length > 0)
const pagination = computed(() => ({
  page: result.value?.manage?.universalReviews?.pagination.currentPage ?? page.value,
  perPage: result.value?.manage?.universalReviews?.pagination.perPage ?? perPage.value,
  total: result.value?.manage?.universalReviews?.pagination.totalCount ?? reviews.value.length,
}))

const csvExportVariables = computed(() => ({
  subjectId: variables.value.subjectId,
  sortBy: variables.value.sortBy,
  sortDirection: variables.value.sortDirection,
  filters: variables.value.filters,
}))

const { load: loadCsvUrl, result: csvUrlResult } = useReviewsAgencyUniversalReviewsExportCsvLazyQuery(csvExportVariables, {
  clientId: 'reviews',
})

async function downloadCsv() {
  track('export_download', { surface: 'dashboard', entity_type: 'service', entity_id: props.agency.id, export_type: 'reviews' })
  try {
    await loadCsvUrl()
    if (csvUrlResult.value?.manage?.universalReviewsExportCsv.errors) {
      showNotification(t('Reviews.agency.reviews.action.csvExportFailed'), 'error')
      return
    }

    if (csvUrlResult.value?.manage?.universalReviewsExportCsv.fileUrl) {
      window.open(csvUrlResult.value.manage.universalReviewsExportCsv.fileUrl, '_blank')
    }
  } catch {
    showNotification(t('Reviews.agency.reviews.action.csvExportFailed'), 'error')
  }
}
</script>
